import { useDispatch, useSelector } from 'react-redux';
import * as React from 'react';

import { TThunkDispatch } from '../../../types/redux';
import { getIsOpen, getShouldAutoOpen } from '../../../selectors/newReview';
import { getNewReviewAccessError } from '../../../selectors/newReview/getNewReviewAccessError';
import { setNewReviewFormClose } from '../../../actions/newReview';
import { getIsAuthenticated } from '../../../selectors/user';
import { getObjectInfo } from '../../../selectors/objectInfo';
import { NEW_REVIEW_AUTO_OPEN_FLAG_CAMEL, NEW_REVIEW_AUTO_OPEN_FLAG_DASH } from '../../../constants/reviews/newReview';
import { requestAuthentication } from '../../../actions/authentication';
import { fetchFormDataAndOpenModalAction } from '../../../actions/newReview/fetchFormDataAndOpenModalAction';
import { DAILY_RENT_SEARCH_URL } from '../../../constants/dailyRentSearchUrl';

export function useDailyrentNewReviewModal() {
  const dispatch = useDispatch<TThunkDispatch>();

  const isOpenedFromState = useSelector(getIsOpen);
  const shouldAutoOpen = useSelector(getShouldAutoOpen);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const rejectAccessError = useSelector(getNewReviewAccessError);
  const objectInfo = useSelector(getObjectInfo);

  const onClose = React.useCallback(() => {
    dispatch(setNewReviewFormClose());
  }, [dispatch]);

  const goToOfferCard = React.useCallback(() => {
    window.open(objectInfo.objectUrl, '_self');
  }, [objectInfo.objectUrl]);

  const onBookClick = React.useCallback(() => {
    window.open(DAILY_RENT_SEARCH_URL, '_self');
  }, []);

  const requestAuth = React.useCallback(() => {
    dispatch(setNewReviewFormClose());
    dispatch(requestAuthentication({ onSuccess: fetchFormDataAndOpenModalAction }));
  }, []);

  const handleReload = React.useCallback(() => {
    window.location.reload();
  }, []);

  React.useLayoutEffect(() => {
    if (!shouldAutoOpen) {
      return;
    }

    // Удаление флага автоматического открытия формы нового отзыва из URL
    const url = new URL(window.location.href);
    const hasFlag =
      url.searchParams.has(NEW_REVIEW_AUTO_OPEN_FLAG_DASH) || url.searchParams.has(NEW_REVIEW_AUTO_OPEN_FLAG_CAMEL);

    if (hasFlag) {
      url.searchParams.delete(NEW_REVIEW_AUTO_OPEN_FLAG_DASH);
      url.searchParams.delete(NEW_REVIEW_AUTO_OPEN_FLAG_CAMEL);
      history.pushState(null, '', url.toString());
    }

    // Если пользователь авторизован - загружаем данные и открываем модалку
    // Если не авторизован - запрашиваем авторизацию, после успеха загрузим данные и откроем модалку
    if (isAuthenticated) {
      dispatch(fetchFormDataAndOpenModalAction());
    } else {
      window.addEventListener('load', () => {
        dispatch(requestAuthentication({ onSuccess: fetchFormDataAndOpenModalAction }));
      });
    }
  }, []);

  return React.useMemo(
    () => ({
      isOpened: isAuthenticated && isOpenedFromState,
      onClose,
      goToOfferCard,
      onBookClick,
      requestAuth,
      rejectAccessError,
      handleReload,
    }),
    [
      isAuthenticated,
      onBookClick,
      isOpenedFromState,
      onClose,
      requestAuth,
      rejectAccessError,
      goToOfferCard,
      handleReload,
    ],
  );
}
